import { useMutation, useQuery } from "@tanstack/react-query";
import axiosInstance from "./axios";
import { ProductSchemaT } from "../components/forms/AddNewProduct";
//  types
import {
  GetAllProductRequestT,
  GetSubCategoryInProductRequestT,
  ProductGetAllResponseT,
  ProductT,
  SalesProduct,
} from "../types/productTypes";
import { PostDataT } from "../types/commonTypes";
import { toast } from "react-toastify";

export type SetProductRackPayload = {
  branchId: number;
  productId: number;
  rackName: string;
  rackLabel: string;
  shelfLabel: string;
};

const normalizeProduct = (product: ProductT): ProductT => ({
  ...product,
  productNameInOtherLanguage:
    product.productNameInOtherLanguage || product.productNameInOtherLang || "",
  mainCategory: product.mainCategory || product.categoryResponse || null,
  subCategory: product.subCategory || product.subCategoryResponse || null,
  rackName: product.rackName || product.rack?.rackName || "",
  rackLabel: product.rackLabel || product.rack?.rackLabel || "",
  shelfLabel: product.shelfLabel || product.rack?.shelfLabel || "",
  availableQuantity: product.availableQuantity ?? product.qty,
  displayQuantity:
    product.displayQuantity ||
    `${Number(product.qty || 0)} ${product.unit || ""}`.trim(),
});

const normalizeProductResponse = (
  response: ProductGetAllResponseT,
): ProductGetAllResponseT => ({
  totalPages: Number(response?.totalPages || 0),
  totalElements: Number(response?.totalElements || 0),
  productResponseList: (response?.productResponseList || []).map(
    normalizeProduct,
  ),
});

export const useAddProduct = () => {
  return useMutation({
    mutationKey: ["useAddProduct"],
    mutationFn: async (postData: ProductSchemaT) => {
      const { data } = await axiosInstance.post("/product", postData);
      return data;
    },
  });
};

export const useUpdateProduct = () => {
  return useMutation({
    mutationKey: ["useUpdateProduct"],
    mutationFn: async (postData: ProductSchemaT) => {
      const { data } = await axiosInstance.put("/product", postData);
      return data;
    },
  });
};

export const useUpdateProductQuantity = () => {
  return useMutation({
    mutationKey: ["useUpdateProductQuantity"],
    mutationFn: async ({
      productId,
      quantity,
    }: {
      productId: string | number;
      quantity: number;
    }) => {
      const { data } = await axiosInstance.put(
        `/product/${productId}/quantity?quantity=${quantity}`,
      );
      return data;
    },
  });
};

export const useGetAProduct = () => {
  return useMutation<ProductT, Error, string>({
    mutationKey: ["useGetAProduct"],
    mutationFn: async (productId: string) => {
      try {
        const { data } = await axiosInstance.get<ProductT>(
          `product/${productId}`,
        );
        return data;
      } catch (error) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch product",
        );
      }
    },
  });
};

export const useDelProduct = () => {
  return useMutation({
    mutationKey: ["useDelProduct"],
    mutationFn: async (productId: string) => {
      const { data } = await axiosInstance.delete(`/product/${productId}`);
      return data;
    },
  });
};

export const useGetAllProduct = (postData: {
  categoryId: number;
  subCategory: number;
}) => {
  return useQuery({
    queryKey: ["useGetAllProduct", postData],
    queryFn: async ({ queryKey }) => {
      const [, postData] = queryKey as [
        string,
        { categoryId: number; subCategory: boolean },
      ];
      const { categoryId, subCategory = false } = postData;
      const { data } = await axiosInstance.get(
        `category/${categoryId}/${subCategory}`,
      );
      return data as ProductGetAllResponseT;
    },
  });
};

export const useGetAllProducts = (postData: PostDataT) => {
  return useQuery({
    queryKey: ["useGetAllProducts", postData],
    queryFn: async () => {
      const { data } = await axiosInstance.get(
        `/product/salesProduct?keyword=${postData.keyword || ""}`,
      );
      return data as SalesProduct[];
    },
    enabled: !!postData,
  });
};

export const useGetSubCategoryInProduct = ({
  categoryId,
  subCategory = true,
}: GetSubCategoryInProductRequestT) => {
  return useQuery({
    queryKey: ["useGetSubCategoryInProduct", categoryId],
    queryFn: async () => {
      const { data } = await axiosInstance.get(
        `category/${categoryId}/${subCategory}`,
      );
      return data as ProductGetAllResponseT;
    },
    enabled: !!categoryId,
  });
};

export const useGetAllProductData = (postData: PostDataT) => {
  return useQuery({
    queryKey: ["useGetAllProduct", postData],
    queryFn: async () => {
      const { data } = await axiosInstance.post<ProductGetAllResponseT>(
        `/product/all`,
        postData,
      );
      return normalizeProductResponse(data);
    },
    enabled: postData.branchId === undefined || postData.branchId > 0,
  });
};

export const useSetProductRack = () => {
  return useMutation({
    mutationKey: ["useSetProductRack"],
    mutationFn: async (postData: SetProductRackPayload) => {
      const { data } = await axiosInstance.post("/product/set-rack", postData);
      return data;
    },
  });
};

export const useDownloadAllProducts = () => {
  return useMutation({
    mutationKey: ["downloadAllProducts"],
    mutationFn: async (postData: GetAllProductRequestT) => {
      const { data } = await axiosInstance.post("/product/all", postData, {
        responseType: "blob",
      });
      return data;
    },
    onSuccess(blob, variables) {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const extension = variables.docType === "EXCEL" ? "xlsx" : "pdf";
      link.href = url;
      link.download = `all-products.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Products downloaded successfully");
    },
  });
};
